import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SettingOutlined } from '@ant-design/icons';
import { Button, Col, List, Row } from 'antd';
import VirtualList from 'rc-virtual-list';

import withErrorHandler from '../../hoc/with-error-handler';
import axios from '../../shared/axios';
import { EventObjectTypeEnum } from '../../shared/constants/enums';
import { AppDispatch, RootState } from '../../store';
import EventSettingsModal from './event-settings-modal';
import { EventActivityFilter, UserActivityEvent } from './models';
import * as actions from './store/actions';
import UserActivityEventItem from './user-activity-event-item';

import './styles.less';

const CONTAINER_HEIGHT = 700;
const PAGE_SIZE = 20;

export type UserActivityEventsProps = {
  objectId: string;
  objectType: EventObjectTypeEnum;
};

export const UserActivityEvents: React.FC<UserActivityEventsProps> = (props) => {
  const dispatch = useDispatch<AppDispatch>();
  const didMount = useRef(false);

  const [pageIndex, setPageIndex] = useState(-1);
  const [componentMounted, setComponentMounted] = useState(false);
  const [localUserActivityEvents, setLocalUserActivityEvents] = useState<UserActivityEvent[]>([]);
  const [listFilter, setListFilter] = useState<EventActivityFilter>({});
  const [showSettingsModal, setShowSettingsModal] = useState<boolean>(false);
  const [noMoreItems, setNoMoreItems] = useState<boolean>(false);

  const { userActivityEventsLoading } = useSelector((state: RootState) => state.userActivityEvents);

  const appendData = async (): Promise<void> => {
    const data = await dispatch(
      actions.getUserActivityEvents({
        pageIndex: pageIndex + 1,
        pageSize: PAGE_SIZE,
        objectId: props.objectId,
        objectType: props.objectType,
        filter: listFilter,
      }),
    );
    if (data.length > 0) {
      setLocalUserActivityEvents((prevData) => [...prevData, ...data]);
      setPageIndex(pageIndex + 1);
    } else {
      setNoMoreItems(true);
    }
  };

  useEffect(() => {
    if (!didMount.current) {
      appendData().then(() => {
        setComponentMounted(true);
      });

      dispatch(actions.getHumanReadableEventTypes());

      didMount.current = true;
    }
  }, []);

  useEffect(() => {
    if (componentMounted) {
      appendData();
    }
  }, [listFilter]);

  const onScroll = (e: React.UIEvent<HTMLElement, UIEvent>) => {
    if (
      !noMoreItems &&
      componentMounted &&
      !userActivityEventsLoading &&
      Math.abs(e.currentTarget.scrollHeight - e.currentTarget.scrollTop - CONTAINER_HEIGHT) <= 1
    ) {
      appendData();
    }
  };

  const userActivityEventsHeader = (
    <div className="user-activity-events-header">
      <Row>
        <Col span={8}></Col>
        <Col span={8} offset={8}>
          <div className="text-right">
            <Button icon={<SettingOutlined />} onClick={() => setShowSettingsModal(true)}></Button>
          </div>
        </Col>
      </Row>
    </div>
  );

  return (
    <div className="user-activity-events-container">
      <List loading={userActivityEventsLoading} bordered={true} header={userActivityEventsHeader} className="user-activity-events-list" split={false}>
        <VirtualList data={localUserActivityEvents} height={CONTAINER_HEIGHT} itemHeight={47} itemKey="id" onScroll={onScroll}>
          {(item: UserActivityEvent) => (
            <List.Item key={item.id}>
              <UserActivityEventItem userActivityEvent={item} />
            </List.Item>
          )}
        </VirtualList>
      </List>

      <EventSettingsModal
        updateListFilter={(newFilter) => {
          setListFilter((prevFilter) => ({ ...prevFilter, ...newFilter }));
        }}
        showSettingsModal={showSettingsModal}
        setShowSettingsModal={setShowSettingsModal}
      />
    </div>
  );
};

export default withErrorHandler(UserActivityEvents, axios);
